import React, {
  useContext,
  useState,
  Fragment,
  useEffect,
  useCallback
} from 'react';
import HeadingAndExpandableBody from '../UI/HeadingAndExpandableBody';
import {Context} from '../../context';
import LabelAndInput from '../UI/LabelAndInput';
import Pagination from '../UI/Pagination';
import Button from '../UI/Button';
import {
  getExpiryDate,
  isAPassNotATicket,
  getAssociatedTransitCardFromTicket,
  getTicketStatus,
  getAllTickets
} from '../../helperFunctions';
import {Link} from 'react-router-dom';

function MyTicketsAndPasses(props) {
  const itemsPerPage = 5;
  const {
    localeText,
    account,
    getTicketName,
    transferPass,
    loading,
    mobileFilter
  } = useContext(Context);
  const [filteredSelection, setFilteredSelection] = useState(
    getAllTickets(account, mobileFilter)
  );
  const [filteredSelectionPaginated, setFilteredSelectionPaginated] = useState(
    []
  );
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    setFilteredSelection(getAllTickets(account, mobileFilter));
  }, [account, mobileFilter]);

  const selectTicket = ticketObj => {
    const transitCard = getAssociatedTransitCardFromTicket(account, ticketObj);
    const ticketObjUuid = transitCard?.tickets?.find(
      ({id}) => id === ticketObj.id
    )?.uuid;

    if (!transitCard) {
      setSelectedTicket(null);
      return;
    }

    setSelectedTicket({
      ...ticketObj,
      transitCardUuid: transitCard.uuid,
      transitCardNickname: transitCard.nickname,
      uuid: ticketObjUuid
    });
  };

  const filterSelection = useCallback(
    status => {
      setFilteredSelection(() =>
        getAllTickets(account, mobileFilter)
          .filter(
            ticketObj =>
              getTicketStatus(localeText, ticketObj) === status ||
              status === 'all'
          )
          .reverse()
      );
    },
    [setFilteredSelection, account.tickets, localeText]
  );

  useEffect(() => {
    filterSelection('all');
  }, [account, filterSelection]);

  const transferPassHandler = () => {
    const selectedTransitCardUuid = document.getElementById(
      'input-selected-transitcard-uuid'
    ).value;

    if (!selectedTransitCardUuid) return;

    const transferPassDetails = {
      transitCardUuid: selectedTransitCardUuid,
      ticketId: selectedTicket.id,
      ticketUuid: selectedTicket.uuid
    };

    transferPass(transferPassDetails);
  };

  return (
    <HeadingAndExpandableBody
      headingAndExpandableBodyId='my-tickets-and-passes'
      heading={localeText(
        'rw_header_my_tickets_and_passes',
        'My Tickets and Passes'
      )}
      expanded={props.expanded}
    >
      {getAllTickets(account, mobileFilter).length > 0 ? (
        <React.Fragment>
          <LabelAndInput
            name='select ticket type'
            className='w-fit mr-0 ml-auto'
            onChange={e => filterSelection(e.target.value)}
            type='select'
          >
            <option value='all'>
              {localeText('rw_title_all_tickets', 'All Products')}
            </option>
            <option value='in use'>
              {localeText('rw_title_tickets_in_use', 'Products in Use')}
            </option>
            <option value='unused'>
              {localeText('rw_title_tickets_unused', 'Products Unused')}
            </option>
            <option value='refunded'>
              {localeText('rw_title_tickets_refunded', 'Products Refunded')}
            </option>
            <option value='expired'>
              {localeText('rw_title_tickets_expired', 'Products Expired')}
            </option>
          </LabelAndInput>
          {filteredSelection?.length > 0 ? (
            <React.Fragment>
              <table
                //* MOBILE View
                id='my-tickets-and-passes-table-mobile'
                className='bg-white sm:hidden border w-full'
              >
                {filteredSelectionPaginated.map((ticketObj, i2) => {
                  return (
                    <tbody
                      onClick={() => selectTicket(ticketObj)}
                      className={`border-b cursor-pointer hover:bg-secondary ${
                        ticketObj.id === selectedTicket?.id && 'bg-secondary'
                      }`}
                      key={i2}
                    >
                      <tr>
                        <td className='px-4 pt-4 align-text-top font-semibold'>
                          {localeText('rw_title_ticket_id', 'Product ID')}
                        </td>
                        <td className='px-4 pt-4 align-text-top'>
                          {ticketObj.id}
                        </td>
                      </tr>
                      <tr>
                        <td className='px-4 align-text-top font-semibold'>
                          {localeText('rw_title_order', 'Order')}
                        </td>
                        <td className='px-4 align-text-top'>
                          {ticketObj?.properties?.orderId || ''}
                        </td>
                      </tr>
                      <tr>
                        <td className='px-4 align-text-top font-semibold'>
                          {localeText('rw_title_description', 'Description')}
                        </td>
                        <td className='px-4 align-text-top'>
                          {getTicketName(ticketObj.productId)}
                        </td>
                      </tr>
                      <tr>
                        <td className='px-4 align-text-top font-semibold'>
                          {localeText('rw_title_transit_card', 'Transit Card')}
                        </td>
                        <td className='px-4 align-text-top'>
                          {getAssociatedTransitCardFromTicket(
                            account,
                            ticketObj
                          )?.nickname || '-'}
                        </td>
                      </tr>
                      <tr>
                        <td className='px-4 whitespace-nowrap align-text-top font-semibold'>
                          {localeText('rw_title_exp', 'Exp')}
                        </td>
                        <td className='px-4'>
                          {getExpiryDate(
                            ticketObj.validToDateTime,
                            ticketObj.sunsetDateTime
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className='px-4 whitespace-nowrap align-text-top font-semibold'>
                          {localeText('rw_title_status', 'Status')}
                        </td>
                        <td className='px-4'>
                          {getTicketStatus(localeText, ticketObj)}
                        </td>
                      </tr>
                      <tr>
                        <td className='px-4 whitespace-nowrap align-text-top font-semibold'>
                          {localeText(
                            'rw_header_usage_history',
                            'Usage History'
                          )}
                        </td>
                        <td className='px-4 pb-4'>
                          <Link to={`/usage-history/${ticketObj.uuid}`}>
                            <i className='fa-solid fa-magnifying-glass'></i>
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  );
                })}
              </table>
              <table
                //* DESKTOP View
                id='my-tickets-and-passes-table'
                className='bg-white w-full hidden sm:table'
              >
                <tbody>
                  <tr className='border-b'>
                    <th className='text-left px-4'>
                      {
                        localeText(
                          'rw_title_ticket_id',
                          'Ticket ID'
                        ) /*TODO update translation to "Ticket ID" in phrase data */
                      }
                    </th>
                    <th className='text-left px-4'>
                      {
                        localeText(
                          'rw_title_description',
                          'Description'
                        ) /*TODO remove "rw_title_ticket_type" from here & phrase data if no longer used */
                      }
                    </th>
                    <th className='text-left px-4'>
                      {localeText('rw_title_transit_card', 'Transit Card')}
                    </th>
                    <th className='text-left px-4'>
                      {localeText('rw_title_exp', 'Exp')}
                    </th>
                    <th className='text-left px-4'>
                      {localeText('rw_title_status', 'Status')}
                    </th>
                    <th className='text-left px-4'>
                      {localeText('rw_header_usage_history', 'Usage History')}
                    </th>
                  </tr>
                  {filteredSelectionPaginated.map((ticketObj, i2) => {
                    return (
                      <tr
                        onClick={() => selectTicket(ticketObj)}
                        className={`border-b cursor-pointer hover:bg-lightGray2 ${
                          ticketObj.id === selectedTicket?.id && 'bg-secondary'
                        }`}
                        key={i2}
                      >
                        <td className='px-4'>{ticketObj.id}</td>
                        <td className='px-4'>
                          {getTicketName(ticketObj.productId)}
                        </td>
                        <td className='px-4'>
                          {getAssociatedTransitCardFromTicket(
                            account,
                            ticketObj
                          )?.nickname || '-'}
                        </td>
                        <td className='px-4'>
                          {getExpiryDate(
                            ticketObj.validToDateTime,
                            ticketObj.sunsetDateTime
                          )}
                        </td>
                        <td className='px-4'>
                          {getTicketStatus(localeText, ticketObj)}
                        </td>
                        <td className='px-4'>
                          <Link to={`/usage-history/${ticketObj.uuid}`}>
                            <i className='fa-solid fa-magnifying-glass'></i>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination
                {...{
                  list: filteredSelection,
                  setPaginatedList: setFilteredSelectionPaginated,
                  itemsPerPage,
                  runOnPageJump: () => setSelectedTicket(null)
                }}
              />
            </React.Fragment>
          ) : (
            <p className='text-center'>No passes to Show</p>
          )}
          {selectedTicket &&
            isAPassNotATicket(selectedTicket.serial) &&
            (getTicketStatus(localeText, selectedTicket) ===
              localeText('rw_title_in_use', 'in use') ||
              getTicketStatus(localeText, selectedTicket) ===
                localeText('rw_title_unused', 'unused')) && (
              <Fragment>
                <div className='text-3xl mt-10'>
                  {localeText('rw_title_transfer_ticket', 'Transfer Product')}
                </div>
                <div className='flex gap-4 items-end flex-wrap'>
                  <div className='pb-4'>
                    {localeText('rw_frag_from', 'From')}{' '}
                    {selectedTicket.transitCardNickname}{' '}
                    {localeText('rw_frag_to', 'to')}
                  </div>
                  <LabelAndInput
                    inputId='selected-transitcard-uuid'
                    name='select transit card'
                    className='grow'
                    type='select'
                    id='select-transit-card'
                  >
                    <option value=''>
                      {localeText('rw_title_select_a_card', 'select a card')}
                    </option>
                    {account.transitAccounts[0].transitCards.map(
                      transitAccount => {
                        if (
                          selectedTicket.transitCardUuid !== transitAccount.uuid
                        )
                          return (
                            <option
                              key={transitAccount.id}
                              value={transitAccount.uuid}
                            >
                              {transitAccount.nickname}
                            </option>
                          );
                        return false;
                      }
                    )}
                  </LabelAndInput>
                  <Button
                    className={`grow py-2 mb-2 ${
                      loading && 'pointer-events-none opacity-50'
                    }`}
                    onClick={transferPassHandler}
                  >
                    {localeText(
                      'rw_button_transfer_ticket',
                      'Transfer Product'
                    )}
                  </Button>
                </div>
              </Fragment>
            )}
        </React.Fragment>
      ) : (
        <p className='text-center'>No passes to Show</p> // TODO add to phrase data
      )}
    </HeadingAndExpandableBody>
  );
}

export default MyTicketsAndPasses;
